// Time-range math for the influx panel: relative chips, the custom datetime-local
// round trip, and the start<end / ≤90d validation mirrored from the worker's bounds.

export const RELATIVE_CHIPS = [
  { key: '15m', ms: 15 * 60 * 1000 },
  { key: '1h', ms: 60 * 60 * 1000 },
  { key: '8h', ms: 8 * 60 * 60 * 1000 },
  { key: '24h', ms: 24 * 60 * 60 * 1000 },
  { key: '7d', ms: 7 * 24 * 60 * 60 * 1000 },
] as const;

export type RelativeChipKey = (typeof RELATIVE_CHIPS)[number]['key'];

export const MAX_RANGE_MS = 90 * 24 * 60 * 60 * 1000;

export type TimeMode = 'relative' | 'custom';
export type RangeError = 'incomplete' | 'invalid' | 'order' | 'tooLong';

/** Resolve a relative chip into an absolute [start, end] pair, end anchored to now. */
export function resolveRelativeRange(ms: number, now: Date = new Date()): { start: string; end: string } {
  const end = now;
  const start = new Date(end.getTime() - ms);
  return { start: start.toISOString(), end: end.toISOString() };
}

/** Format a Date as a `datetime-local` input value (local time, minute precision). */
export function toDatetimeLocalValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Validate a pair of `datetime-local` input values against the worker's bounds. */
export function validateCustomRange(startValue: string, endValue: string): RangeError | null {
  if (!startValue || !endValue) return 'incomplete';
  const start = new Date(startValue);
  const end = new Date(endValue);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 'invalid';
  if (start.getTime() >= end.getTime()) return 'order';
  if (end.getTime() - start.getTime() > MAX_RANGE_MS) return 'tooLong';
  return null;
}

/** ISO conversion for a validated `datetime-local` pair. */
export function customRangeToIso(startValue: string, endValue: string): { start: string; end: string } {
  return { start: new Date(startValue).toISOString(), end: new Date(endValue).toISOString() };
}

/** The "dernière activité" shortcut: a 1h window ending at the machine's last data point. */
export function lastActivityRange(lastTime: string): { start: string; end: string } {
  const end = new Date(lastTime);
  const start = new Date(end.getTime() - RELATIVE_CHIPS[1].ms); // 1h
  return { start: toDatetimeLocalValue(start), end: toDatetimeLocalValue(end) };
}
